import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
	MDHeaderComponent,
	MDTagComponent,
	MDButtonGroupComponent,
	MDButtonToggleComponent,
	MDIconComponent,
	MDContentBoxComponent,
	MD_DIALOG_REF,
	MDDialogRef
} from '@melodic-ui/components';
import { IndicatorType } from '@melodic-ui/types';
import { IGradingTemplateCategoryDetail, IGradingTemplateCriteriaDetail } from '@lb/api/core/interfaces';

/** A single rubric row. Category rubrics and project criteria rubrics both satisfy this shape. */
export interface IViewRubricRow {
	rubricID: number;
	gradeName: string;
	gradeColor: IndicatorType;
	description: string;
}

/** One tab of the dialog — a grading category for daily grades, a criterion for the project. */
export interface IViewRubricTab {
	id: number;
	label: string;
	/**
	 * How much of the grade this tab carries, in percent. What it is a percent *of* depends on the
	 * dialog's `weightScope`: category weights are validated to sum to 100 across the whole template,
	 * while project criteria weights sum to 100 within the project category alone.
	 */
	weightPercent: number;
	rubrics: IViewRubricRow[];
}

/** Daily grades: one tab per per-lesson grading category. Weights are shares of the overall grade. */
export function toRubricTabsFromCategories(categories: IGradingTemplateCategoryDetail[]): IViewRubricTab[] {
	return categories.map((category) => ({
		id: category.gradingTemplateCategoryID,
		label: category.categoryName,
		weightPercent: category.weightPercent,
		rubrics: category.rubrics
	}));
}

/**
 * Project: one tab per criterion, in the same order the grading dialog steps through them. Weights
 * are shares of the *project* grade — the project category's own share of the overall grade is a
 * separate number, passed to the dialog as `projectWeightPercent`.
 */
export function toRubricTabsFromCriteria(criteria: IGradingTemplateCriteriaDetail[]): IViewRubricTab[] {
	return [...criteria]
		.sort((a, b) => a.orderNumber - b.orderNumber)
		.map((criterion) => ({
			id: criterion.criteriaID,
			label: criterion.criteriaName,
			weightPercent: criterion.weightPercent,
			rubrics: criterion.rubrics
		}));
}

/**
 * Dialog inputs for the project rubric reference, shared by every block that offers it (project
 * progress on a regular lesson, project results on the final lesson). Criteria weights sum to 100
 * within the project category, not across the template, so the dialog must not present them as
 * shares of the overall grade — the category's own weight is what ties the project back to it.
 */
export function toProjectRubricDialogInputs(projectCategory: IGradingTemplateCategoryDetail | null | undefined): Record<string, unknown> {
	return {
		tabs: toRubricTabsFromCriteria(projectCategory?.criteria ?? []),
		heading: 'Project Rubrics',
		tabNoun: 'criterion',
		tabNounPlural: 'criteria',
		weightScope: 'project',
		projectWeightPercent: projectCategory?.weightPercent ?? null
	};
}

/**
 * Read-only rubric reference. Shows the grade descriptions for one tab at a time and never assigns
 * a grade — grading happens in the daily grade dropdowns and the semester project grade dialog.
 * Offered to everyone who can see the grades, learners included: the rubric is the explanation of
 * the grade, not an editing affordance, and this dialog has no write path.
 */
@Component({
	selector: 'lb-view-rubric-dialog',
	templateUrl: './view-rubric-dialog.component.html',
	styleUrl: './view-rubric-dialog.component.scss',
	imports: [DecimalPipe, MDHeaderComponent, MDTagComponent, MDButtonGroupComponent, MDButtonToggleComponent, MDIconComponent, MDContentBoxComponent],
	standalone: true
})
export class ViewRubricDialogComponent implements OnInit {
	private readonly dialogRef = inject<MDDialogRef>(MD_DIALOG_REF);

	tabs = input.required<IViewRubricTab[]>();
	heading = input<string>('Rubrics');
	/** What a tab represents, used in the empty-state copy ("No rubrics defined for this category."). */
	tabNoun = input<string>('category');
	/** Plural of tabNoun, for the weighting note. Defaults to tabNoun + 's'. */
	tabNounPlural = input<string | null>(null);
	/**
	 * What the tab weights are a share of. 'overall' = the whole semester grade (grading categories);
	 * 'project' = the project grade alone (project criteria, which are weighted against each other
	 * and then carried into the overall grade by the project category's own weight).
	 */
	weightScope = input<'overall' | 'project'>('overall');
	/** For weightScope 'project': the project category's share of the overall grade. */
	projectWeightPercent = input<number | null>(null);
	/**
	 * What accounts for the weight this dialog does not list, when the listed tabs fall short of 100%.
	 * The daily-grades dialog omits the project category, so its remainder lives there.
	 */
	unlistedWeightLabel = input<string>('other grading categories');

	selectedTabID = signal<number | null>(null);

	selectedTab = computed(() => {
		const tabID = this.selectedTabID();
		return this.tabs().find((t) => t.id === tabID) ?? null;
	});

	private readonly nounPlural = computed(() => this.tabNounPlural() ?? `${this.tabNoun()}s`);

	/** What a single tab's weight is a percentage of, for the per-tab line. */
	readonly weightScopeLabel = computed(() => (this.weightScope() === 'project' ? 'project grade' : 'overall grade'));

	/** Total weight of the tabs actually listed here. */
	readonly listedWeightTotal = computed(() => this.tabs().reduce((sum, tab) => sum + (tab.weightPercent ?? 0), 0));

	/**
	 * Weight that exists but isn't shown in this dialog. Only meaningful in the 'overall' scope, where
	 * the template's categories are validated to sum to 100 — anything missing is a category the
	 * caller filtered out (the project). Rounded to avoid a 0.001% remainder producing a note.
	 */
	readonly unlistedWeightPercent = computed(() => {
		if (this.weightScope() !== 'overall') {
			return 0;
		}

		return Math.max(0, Math.round((100 - this.listedWeightTotal()) * 100) / 100);
	});

	/**
	 * The weighting note under the rubric grid. Says what the listed tabs add up to and — when they
	 * fall short of the whole grade — that the rest is graded elsewhere, so nobody reads these
	 * percentages as the complete picture. Empty when there is no weight information to give.
	 */
	readonly weightNote = computed(() => {
		const tabs = this.tabs();
		if (tabs.length === 0) {
			return '';
		}

		if (this.weightScope() === 'project') {
			const projectWeight = this.projectWeightPercent();
			const base = `Each criterion's percentage is its share of the project grade.`;

			return projectWeight != null
				? `${base} The project as a whole is worth ${this.formatWeight(projectWeight)}% of the overall grade.`
				: base;
		}

		const unlisted = this.unlistedWeightPercent();
		if (unlisted <= 0) {
			return `These ${this.nounPlural()} account for the full overall grade.`;
		}

		return (
			`These ${this.nounPlural()} account for ${this.formatWeight(this.listedWeightTotal())}% of the overall grade. ` +
			`The remaining ${this.formatWeight(unlisted)}% comes from ${this.unlistedWeightLabel()}.`
		);
	});

	/** Trims a weight to at most 2 decimals without printing trailing zeros ("20", not "20.00"). */
	private formatWeight(value: number): string {
		return `${Math.round(value * 100) / 100}`;
	}

	ngOnInit(): void {
		if (this.tabs().length > 0) {
			this.selectedTabID.set(this.tabs()[0].id);
		}
	}

	selectTab(tabID: number): void {
		this.selectedTabID.set(tabID);
	}

	close(): void {
		this.dialogRef.close();
	}
}
